import type { SessionLifecycleObserver } from '@core/ports/SessionLifecycleObserver';
import { BffTransportError } from '@infrastructure/http/BffTransportError';
import { SessionLifecycleTransportBridge } from '@infrastructure/http/SessionLifecycleTransportBridge';
import { BrowserConnectivityRuntime } from '@infrastructure/runtime/BrowserConnectivityRuntime';

export class AuthenticatedBffTransport {
  private static readonly sessionRequiredMessage = 'Start this journey from the NHS App to continue.';
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable.';

  private readonly lifecycleBridge: SessionLifecycleTransportBridge;
  private readonly connectivityRuntime: BrowserConnectivityRuntime;

  public constructor(
    sessionLifecycleObserver?: SessionLifecycleObserver,
    connectivityRuntime = new BrowserConnectivityRuntime(),
  ) {
    this.lifecycleBridge = new SessionLifecycleTransportBridge(sessionLifecycleObserver);
    this.connectivityRuntime = connectivityRuntime;
  }

  public async fetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    if (!this.connectivityRuntime.isOnline()) {
      throw new BffTransportError('offline', AuthenticatedBffTransport.offlineMessage);
    }

    let response: Response;

    try {
      response = await fetch(input, {
        ...init,
        credentials: 'include',
        headers,
      });
    } catch (error) {
      if (!this.connectivityRuntime.isOnline()) {
        throw new BffTransportError('offline', AuthenticatedBffTransport.offlineMessage);
      }

      throw error;
    }

    if (response.status === 401) {
      this.lifecycleBridge.markSessionRequired(AuthenticatedBffTransport.sessionRequiredMessage);
      return response;
    }

    this.lifecycleBridge.applyResponseHeaders(response.headers);
    return response;
  }
}